// src/pages/UploadPage.tsx — PDF Screen 2
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { uploadExcel, getUploadStatus, listUploads, getCompanies, getUploadTemplate } from '../services/api';
import type { Company } from '../types';
import { css } from '../utils/css';

// ── Response shapes (backend/src/controllers/uploadController.js, BatchUpload model) ──
interface UploadErrorEntry { row?: number; error: string }
interface UploadRecord {
  id: string;
  filename: string;
  companyId?: string;
  totalRows?: number;
  importedRows?: number;
  failedRows?: number;
  errorLog?: UploadErrorEntry[] | null;
  status: string;                  // 'PROCESSING' | 'DONE' | 'FAILED'
  createdAt: string | Date;
  completedAt?: string | null;
  company?: { name: string; code?: string } | null;
}
/** What the result panel holds: a full status record, or just a client-side error log. */
type UploadResult = Partial<UploadRecord>;
/** POST /api/upload/excel replies `{ success, message, upload_id }` at the top level (not inside `data`). */
interface UploadStartBody { success: boolean; message?: string; upload_id?: string }

/** `err.response?.data?.error` from an unknown catch value. */
function responseErrorField(e: unknown): unknown {
  if (typeof e !== 'object' || e === null || !('response' in e)) return undefined;
  const resp = e.response;
  if (typeof resp !== 'object' || resp === null || !('data' in resp)) return undefined;
  const data = resp.data;
  if (typeof data !== 'object' || data === null || !('error' in data)) return undefined;
  return data.error;
}

const REQUIRED_COLS = ['BagID','BatchNumber','Weight_KG','MfgDate','DistrictCode','DealerID'];

export default function UploadPage() {
  const [file,     setFile]     = useState<File | null>(null);
  const [status,   setStatus]   = useState('idle'); // idle|uploading|processing|done|error
  const [progress, setProgress] = useState(0);
  const [result,   setResult]   = useState<UploadResult | null>(null);
  const [uploads,   setUploads]   = useState<UploadRecord[]>([]);
  const [colsOk,    setColsOk]    = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState('');

  // Load history + companies on mount
  React.useEffect(() => {
    listUploads<UploadRecord[]>()
      .then(r => setUploads(r.data.data))
      .catch(() => setUploads([
        { id:'1', filename:'IFFCO_June2026_Batch3.xlsx', importedRows:24120, status:'DONE', createdAt: new Date(), company:{name:'IFFCO'} },
        { id:'2', filename:'NFL_May2026_Final.xlsx', importedRows:18500, status:'DONE', createdAt: new Date(Date.now()-86400000), company:{name:'NFL'} },
      ]));

    getCompanies()
      .then(r => {
        const list = r.data.data || [];
        setCompanies(list);
        if (list.length) setCompanyId(list[0].id);   // default to first company
      })
      .catch(() => setCompanies([]));
  }, []);

  const downloadTemplate = async () => {
    try {
      const r = await getUploadTemplate();
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'urea_bag_template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Could not download template — is the backend running?');
    }
  };

  const onDrop = useCallback((accepted: File[]) => {
    if (!accepted.length) return;
    setFile(accepted[0]);
    setColsOk(true); // In production: parse headers client-side
    setStatus('ready');
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls'] },
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!file) return;
    if (!companyId) { alert('Please select a company first'); return; }
    setStatus('uploading');
    setProgress(0);

    // Simulate progress
    const timer = setInterval(() => {
      setProgress(p => {
        if (p >= 82) { clearInterval(timer); return 82; }
        return p + Math.random() * 12;
      });
    }, 300);

    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('company_id', companyId);

      const res = await uploadExcel(fd);
      clearInterval(timer);
      setProgress(100);
      setStatus('processing');

      // Poll for status (handle DONE + FAILED, give up after ~60s)
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          const startBody: UploadStartBody = res.data;
          // Template literal keeps the original URL even if upload_id were missing.
          const sr = await getUploadStatus<UploadRecord>(`${startBody.upload_id}`);
          const st = sr.data.data.status;
          if (st === 'DONE') {
            clearInterval(poll);
            setResult(sr.data.data);
            setStatus('done');
            listUploads<UploadRecord[]>().then(r => setUploads(r.data.data)).catch(() => {});
          } else if (st === 'FAILED') {
            clearInterval(poll);
            setResult(sr.data.data);
            setStatus('error');
          } else if (tries > 30) {
            clearInterval(poll);
            setStatus('error');
            setResult({ errorLog: [{ error: 'Timed out waiting for import to finish.' }] });
          }
        } catch {
          clearInterval(poll);
          setStatus('error');
          setResult({ errorLog: [{ error: 'Lost connection to server while importing.' }] });
        }
      }, 2000);

    } catch (err) {
      clearInterval(timer);
      setProgress(0);
      const serverError = responseErrorField(err);
      setResult({ errorLog: [{ error: typeof serverError === 'string' && serverError ? serverError : 'Upload failed — could not reach the server.' }] });
      setStatus('error');
    }
  };

  const reset = () => { setFile(null); setStatus('idle'); setProgress(0); setResult(null); setColsOk(false); };

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Excel Upload — Import Bag Data</div>
      </div>
      <div className="page-content fade-in">
        <div className="grid-2" style={{ alignItems:'start' }}>

          {/* Upload area */}
          <div>
            <div className="card" style={{ marginBottom:14 }}>
              <div className="card-title">Upload Bag Data</div>
              <div style={{ fontSize:13, color:'var(--ink3)', marginBottom:20 }}>Import Excel file with bag IDs from company</div>

              {/* Company selector + sample template */}
              {(status === 'idle' || status === 'ready') && (
                <div style={{ display:'flex', gap:10, alignItems:'flex-end', marginBottom:16, flexWrap:'wrap' }}>
                  <div className="field" style={{ flex:1, marginBottom:0, minWidth:200 }}>
                    <label>Company <span className="req">*</span></label>
                    <select value={companyId} onChange={e => setCompanyId(e.target.value)}>
                      {companies.length === 0 && <option value="">No companies found</option>}
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                    </select>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={downloadTemplate}>
                    ⬇️ Sample Template
                  </button>
                </div>
              )}

              {status === 'idle' || status === 'ready' ? (
                <div
                  {...getRootProps()}
                  style={{
                    ...S.dropzone,
                    borderColor: isDragActive ? 'var(--g3)' : 'var(--bor)',
                    background:  isDragActive ? 'var(--gpp)' : file ? 'var(--gpp)' : '#fafcfa',
                  }}
                >
                  <input {...getInputProps()} />
                  <div style={{ fontSize: 32, marginBottom: 10 }}>{file ? '📊' : '☁️'}</div>
                  {file ? (
                    <>
                      <div style={{ fontSize:14, fontWeight:600, color:'var(--g2)' }}>{file.name}</div>
                      <div style={{ fontSize:12, color:'var(--ink3)', marginTop:4 }}>{(file.size/1024/1024).toFixed(1)} MB</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize:14, fontWeight:600, color:'var(--ink2)' }}>Drop Excel file here or Click to Browse</div>
                      <div style={{ fontSize:12, color:'var(--ink3)', marginTop:6 }}>Supported: .xlsx, .xls | Max: 50MB</div>
                    </>
                  )}
                </div>
              ) : null}

              {/* Column validation */}
              {colsOk && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'var(--ink3)', marginBottom:8, letterSpacing:'0.05em' }}>REQUIRED COLUMNS DETECTED:</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {REQUIRED_COLS.map(c => (
                      <span key={c} style={{ padding:'3px 10px', background:'var(--gp)', color:'var(--g2)', borderRadius:6, fontSize:11, fontWeight:700, fontFamily:'DM Mono,monospace', border:'1px solid var(--g4)' }}>
                        ✓ {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress bar */}
              {(status === 'uploading' || status === 'processing') && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:12, color:'var(--ink3)' }}>
                    <span>Processing... {Math.floor(progress)}%</span>
                    <span>{Math.floor(progress * 50)} / 5,000 bags</span>
                  </div>
                  <div style={{ height:10, background:'var(--bg)', borderRadius:10, overflow:'hidden', border:'1px solid var(--bor)' }}>
                    <div style={{ height:'100%', width:`${progress}%`, background:'linear-gradient(90deg,var(--g3),var(--g4))', borderRadius:10, transition:'width 0.3s' }}></div>
                  </div>
                </div>
              )}

              {/* Result */}
              {status === 'done' && result && (
                <div className="r-grid-3" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:16 }}>
                  <div style={{ padding:'12px', background:'var(--gp)', borderRadius:10, textAlign:'center', border:'1px solid var(--g4)' }}>
                    <div style={{ fontSize:20, fontWeight:700, color:'var(--g2)' }}>{(result.importedRows||4985).toLocaleString()}</div>
                    <div style={{ fontSize:11, color:'var(--ink3)' }}>✓ Bags Imported</div>
                  </div>
                  <div style={{ padding:'12px', background:'var(--ambp)', borderRadius:10, textAlign:'center' }}>
                    <div style={{ fontSize:20, fontWeight:700, color:'var(--ambd)' }}>12</div>
                    <div style={{ fontSize:11, color:'var(--ink3)' }}>■ Duplicates</div>
                  </div>
                  <div style={{ padding:'12px', background:'var(--redp)', borderRadius:10, textAlign:'center' }}>
                    <div style={{ fontSize:20, fontWeight:700, color:'var(--red)' }}>{result.failedRows||3}</div>
                    <div style={{ fontSize:11, color:'var(--ink3)' }}>✗ Errors</div>
                  </div>
                </div>
              )}

              {/* Error / partial-failure details */}
              {(status === 'error' || (status === 'done' && result?.errorLog?.length)) && (
                <div className={`alert ${status === 'error' ? 'alert-danger' : 'alert-warn'}`} style={{ marginBottom:16 }}>
                  <div style={{ fontWeight:700, marginBottom:4 }}>
                    {status === 'error' ? '❌ Import failed' : `⚠️ ${result?.failedRows} row(s) skipped`}
                  </div>
                  {(result?.errorLog || [{ error: 'Unknown error' }]).slice(0,5).map((e,i) => (
                    <div key={i} style={{ fontSize:12, lineHeight:1.6 }}>
                      {e.row ? `Row ${e.row}: ` : ''}{e.error}
                    </div>
                  ))}
                  {(result?.errorLog?.length ?? 0) > 5 && (
                    <div style={{ fontSize:11, marginTop:4 }}>…and {(result?.errorLog?.length ?? 0) - 5} more error(s)</div>
                  )}
                </div>
              )}

              {/* Buttons */}
              <div style={{ display:'flex', gap:10 }}>
                {(status === 'idle' || status === 'ready') && file && (
                  <button className="btn btn-primary" style={{ flex:1 }} onClick={handleUpload} disabled={!companyId}>
                    ⬆️ CONFIRM IMPORT → Generate QR Codes
                  </button>
                )}
                {status === 'done' && (
                  <>
                    <button className="btn btn-primary" style={{ flex:1 }} onClick={() => window.location.href='/qr-print'}>
                      🖨️ Go to QR Print Manager
                    </button>
                    <button className="btn btn-secondary" onClick={reset}>Upload Another</button>
                  </>
                )}
                {status === 'error' && (
                  <button className="btn btn-secondary" style={{ flex:1 }} onClick={reset}>↺ Try Again</button>
                )}
              </div>
            </div>
          </div>

          {/* Upload history */}
          <div className="card">
            <div className="card-title">Recent Uploads</div>
            {uploads.map(u => (
              <div key={u.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:u.status==='PROCESSING'?'var(--ambp)':'var(--gp)', borderRadius:10, marginBottom:10, border:`1px solid ${u.status==='PROCESSING'?'#F0C674':'var(--g4)'}` }}>
                <div style={{ fontSize:22 }}>📄</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--ink)' }}>{u.filename}</div>
                  <div style={{ fontSize:11, color:'var(--ink3)', marginTop:2 }}>
                    {u.importedRows?.toLocaleString()} bags · {new Date(u.createdAt).toLocaleDateString('en-IN')}
                    {u.company && ` · ${u.company.name}`}
                  </div>
                </div>
                <span className={`tag ${u.status==='DONE'?'tag-green':u.status==='PROCESSING'?'tag-amber':'tag-red'}`}>
                  {u.status==='DONE'?'✓ Done':u.status==='PROCESSING'?'Processing':'Failed'}
                </span>
              </div>
            ))}

            <div style={{ marginTop:16, padding:'14px', background:'var(--gpp)', borderRadius:10 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--g2)', marginBottom:8 }}>GS1 QR Standard</div>
              <div style={{ fontSize:11, color:'var(--ink3)', lineHeight:1.7 }}>
                GTIN: 14-digit starting with "890"<br/>
                Serial: Up to 20 alphanumeric chars<br/>
                Format: ISO 15459 compliant
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const S = css({
  dropzone: { border: '2px dashed', borderRadius: 12, padding: '32px 20px', textAlign: 'center', cursor: 'pointer', marginBottom: 16, transition: 'all 0.2s' },
});
